// What is a Glue resource? Essentially it's the basic building block of the Spryker Glue API
// - can have its own endpoint/set of endpoints, e.g. GET /carts, POST /carts -> resource: carts
// --- also as a sub-resource/child of another resource, e.g. POST /carts/:id/items -> resource: items
// - can be used as the 'type' to describe the attribute structure in a Glue request
// --- e.g. POST /carts {data: {type: carts, attributes: ...}}
// - can be used as the 'type' to describe the attribute structure in a Glue response
// --- e.g. GET /stores -> {data: [{type: stores, attributes: ...}]}
// - can be added in the 'include' parameter of another resource, e.g. GET /carts?include=items -> resource: items
// --- also means it can appear in the 'relationships' section of a Glue response
export enum EGlueResource {
  // products-related
  ABSTRACT_PRODUCTS               = 'abstract-products',
  CONCRETE_PRODUCTS               = 'concrete-products',
  ABSTRACT_PRODUCT_IMAGE_SETS     = 'abstract-product-image-sets',
  CONCRETE_PRODUCT_IMAGE_SETS     = 'concrete-product-image-sets',
  ABSTRACT_PRODUCT_PRICES         = 'abstract-product-prices',
  CONCRETE_PRODUCT_PRICES         = 'concrete-product-prices',
  ABSTRACT_PRODUCT_AVAILABILITIES = 'abstract-product-availabilities',
  CONCRETE_PRODUCT_AVAILABILITIES = 'concrete-product-availabilities',
  PRODUCT_LABELS                  = 'product-labels',

  // customer management-related
  CUSTOMERS                       = 'customers',
  CUSTOMER_PREFERENCES            = 'customer-preferences',
  COMPANIES                       = 'companies',
  COMPANY_BUSINESS_UNITS          = 'company-business-units',
  COMPANY_BUSINESS_UNIT_ADDRESSES = 'company-business-unit-addresses',
  COMPANY_ROLES                   = 'company-roles',
  COMPANY_USERS                   = 'company-users',
  COMPANY_APPROVERS               = 'company-approvers',
  ADDRESSES                       = 'addresses',
  ADDRESSES_REPORT_WRONG          = 'addresses-report-wrong',
  CPQ_ADDRESS_REPORT_WRONG        = 'cpq-address-report-wrong',

  // catalog and common shopping flow features-related
  CATALOG_SEARCH                  = 'catalog-search',
  CATALOG_SEARCH_SUGGESTIONS      = 'catalog-search-suggestions',
  CATEGORY_TREES                  = 'category-trees',
  CATEGORY_NODES                  = 'category-nodes',
  CARTS                           = 'carts',
  GUEST_CARTS                     = 'guest-carts',
  ITEM_LISTS                      = 'item-lists',
  CART_ITEMS                      = 'items',
  CART_RULES                      = 'cart-rules',
  VOUCHERS                        = 'vouchers',
  CARTS_EXPORT_PDF                = 'export-to-pdf',
  SDM_IB_COMPONENTS               = 'sdm-ib-components',
  EXTERNAL_CARTS                  = 'external-carts',

  // classic flow with or without approval and with checkout-related
  CARTS_APPROVAL                  = 'carts-approval',
  QUOTE_DECLINE                   = 'quote-decline',

  CHECKOUT                        = 'checkout',
  CHECKOUT_UPDATE                 = 'checkout-update',
  CHECKOUT_DATA                   = 'checkout-data',
  SHIPMENTS                       = 'shipments',
  SHIPMENT_METHODS                = 'shipment-methods',
  ORDERS                          = 'orders',
  ORDERS_CANCEL                   = 'orders-cancel',
  ORDERS_EXPORT_EXCEL             = 'orders-export-to-excel',

  // RfQ flow-related
  RFQ_PRODUCTS                    = 'rfq-products',
  QUOTE_REQUESTS                  = 'quote-requests',
  QUOTE_REQUESTS_CANCEL           = 'quote-request-cancel',
  QUOTE_REQUESTS_EXPORT_PDF       = 'generate-pdf',

  // CPQ flow-related
  CPQ_INITIALIZE                  = 'cpq-initialize',
  CPQ_GUIDED_INITIALIZE           = 'cpq-guided-initialize',
  CPQ_SET_STEP                    = 'cpq-set-step',
  CPQ_ACCEPT                      = 'cpq-accept',
  CPQ_COMMIT                      = 'cpq-commit',
  CPQ_UNCOMMIT                    = 'cpq-uncommit',
  CPQ_RECONFIGURE                 = 'cpq-reconfigure',
  CPQ_RECALCULATE                 = 'cpq-recalculate',
  CPQ_QUOTE                       = 'cpq-quote',
  CPQ_QUOTE_ITEMS                 = 'cpq-quote-items',
  CPQ_QUOTE_ITEMS_COMMIT          = 'cpq-cart-items-commit',
  CPQ_QUOTE_DOWNLOAD              = 'cpq-quote-download',
  CPQ_CONFIGURATION_DOWNLOAD      = 'cpq-configuration-download',
  CPQ_CONTRACT_DETAILS_DEFAULTS   = 'cpq-contract-details-defaults',
  CPQ_SUMMARY                     = 'cpq-summary',


  // TpF/OPAL/SAP P40/other systems integration
  SHS_EQUIPMENT_MATERIALS         = 'shs-equipment-materials',
  SHS_EQUIPMENTS                  = 'shs-equipments',
  SHS_EQUIPMENTS_INSTALLED_BASE   = 'shs-equipments-installed-base',
  OPAL_SYSTEMS_SEARCH             = 'opal-systems-search',
  CUSTOMER_ORDERS_HISTORY         = 'customer-orders-history',
  SAP_MESSAGES                    = 'sap-messages',
  SAP_ITEM_AVAILABILITIES         = 'sap-item-availabilities',
  SOLD_TO_ADDRESSES               = 'sold-to-addresses',
  SHIP_TO_ADDRESSES               = 'ship-to-addresses',
  BILL_TO_ADDRESSES               = 'bill-to-addresses',
  PAYER_ADDRESSES                 = 'payer-addresses',
  CUSTOMER_SOLD_TO_ID             = 'customer-sold-to-id',

  // misc.
  STORES                          = 'stores',
  NAVIGATIONS                     = 'navigations',
  CMS_PAGES                       = 'cms-pages',
  CONTENT_BANNERS                 = 'content-banners',
  CONTENT_BANNERS_BY_KEY          = 'content-banners-by-key',
  CONTENT_PRODUCT_ABSTRACT        = 'content-product-abstract',
  CONTACT_US                      = 'contact-us',
  CONTACT_US_GENERAL              = 'contact-us-general',
}

// HTTP query params usable for all/most Glue endpoints
export enum EQueryParams {
  // common
  INCLUDE = 'include',

  // pagination-related for specific API endpoints (GET /orders, GET /catalog-search, ...)
  OFFSET  = 'page[offset]',
  LIMIT   = 'page[limit]',

  // sort-related for specific API endpoints (GET /orders, GET /catalog-search, ...)
  SORT    = 'sort',
}

export enum ETranslationTypes {
  LABEL,
  DESCRIPTION,
  TITLE,
  SUBTITLE
}

export enum ERouteDataTypes {
  ROUTE_DATA_BREADCRUMB   = 'breadcrumb',
  DYNAMIC_DATA_BREADCRUMB = 'dynamicBreadCrumb',
  SHOW_DATA_BREADCRUMB    = 'showBreadcrumb',
  IS_BREADCRUMB_DISABLED  = 'breadcrumbDisabled'
}

export enum EUserRoles {
  Guest                   = 'GUEST',
  Viewer                  = 'RIGHT_WEBSHOPS_BROWSER_USER', // 'VIEWER',
  SPCViewer               = 'RIGHT_WEBSHOPS_SPAREPARTS_VIEW', // 'SPCVIEWER',
  Buyer                   = 'RIGHT_WEBSHOPS_ORDER_USER', // 'BUYER',
  Approver                = 'RIGHT_WEBSHOPS_APPROVER_USER', // 'APPROVER', // 'RIGHT_WEBSHOPS_APPROVER_USER',
  Admin                   = 'RIGHT_WEBSHOPS_CUSTOMER_ADMIN', // 'ADMIN'
  BusinessPartnerBuyer    = 'RIGHT_WEBSHOPS_BUSINESSPARTNER_BUYER',
  BusinessPartnerApprover = 'RIGHT_WEBSHOPS_BUSINESSPARTNER_APPROVER',
}

export enum EAppBarAlertType {
  NOT_LOGGED_USER,
  RESTRICTED_ACCESS,
  AUTHENTICATED_GUEST,
}

export enum ECompanyBUIncludes {
  COMPANY_BUSINESS_UNITS = 'company-business-units',
  CUSTOMERS              = 'customers',
  COMPANY_ROLES          = 'company-roles',
  CUSTOMER_PREFERENCES   = 'customer-preferences'
}

export enum EStoreTypes {
  AU = 'AU',
  BR = 'BR',
  CA = 'CA',
  GB = 'GB',
  IN = 'IN',
  JP = 'JP',
  US = 'US',
  EU = 'EU',
  DK = 'DK',
  MEA = 'MEA',
}

export enum ELanguageTypes {
  EN = 'en',
  FR = 'fr',
  JA = 'ja',
  PT = 'pt',
}

export enum EStoreFeatures {
  CPQ              = 'cpq',
  SPARE_PARTS      = 'spareParts',
  MY_PROFILE       = 'myProfile',
  MULTI_CART       = 'multiCart',
  EXCLUDE_TAX      = 'excludeTax',
  SHOP_BY_CATEGORY = 'shopByCategory',
  WISHLISTS        = 'wishlists',
  PRODUCT_TAGS     = 'productTags',
}

export const CARTS_API_ERROR_CODES_TO_SHOW_ALERT: string[] = ['102', '113', '115', '192', '193', '901'];

export enum EFullPageLoaderListOptions {
  NAVIGATION      = 'NAVIGATION',
  HOMEPAGE_BANNER = 'HOMEPAGE_BANNER'
}

export const HOMEPAGE_FULL_PAGE_LOADER_LIST: EFullPageLoaderListOptions[] = [
  EFullPageLoaderListOptions.NAVIGATION,
  EFullPageLoaderListOptions.HOMEPAGE_BANNER,
];

export enum EProfileTabs {
  PERSONAL = 'personal-data',
  ACCOUNT  = 'account-settings',
  DELIVERY = 'delivery-addresses'
}

export enum EInstalledBaseTabs {
  EQUIPMENT = 'my-equipment',
  CONTRACTS = 'my-contracts'
}

export enum EMultiCartTabs {
  PARTS    = 'parts',
  SERVICES = 'services'
}

export enum ESparePartsOrderHistoryTabs {
  ORDERS  = 'orders',
  ARCHIVE = 'archive'
}

export enum HospitalOrdersTabs {
  PREVIOUS_ORDERS   = 'approver-previous-orders',
  PENDING_ORDERS    = 'approver-pending-orders',
  PREVIOUS_QUOTES   = 'previous-quotes',
  PREVIOUS_REQUESTS = 'previous-request',
}

export enum EPurchaseActivityTabs {
  PREVIOUS_ORDERS   = 'previous-orders',
  PENDING_ORDERS    = 'pending-orders',
  PREVIOUS_QUOTES   = 'previous-quotes',
  PREVIOUS_REQUESTS = 'previous-requests',
  SERVICES          = 'services'
}

export enum EPurchaseActivityPreviousOrdersTabs {
  ORDERS  = 'orders',
  ARCHIVE = 'archive'
}

export enum EMyInstalledBaseTabs {
  EQUIPMENTS = 'equipments',
  CONTRACTS  = 'contracts'
}

export enum EMyInstalledBaseSections {
  SELECTABLE               = 'selectable',
  OTHER                    = 'other',
  NEEDS_ATTENTION          = 'needsAttention',
  UNDER_CONTRACT_CONTRACTS = 'underContractContracts'
}

export const EMyInstalledBaseContent: Record<EMyInstalledBaseTabs, EMyInstalledBaseSections[]> = {
  [EMyInstalledBaseTabs.EQUIPMENTS]: [EMyInstalledBaseSections.SELECTABLE, EMyInstalledBaseSections.OTHER],
  [EMyInstalledBaseTabs.CONTRACTS]: [EMyInstalledBaseSections.NEEDS_ATTENTION, EMyInstalledBaseSections.UNDER_CONTRACT_CONTRACTS],
};

export enum EMyInstalledBaseEquipmentFilters {
  MODALITY         = 'modality',
  EQUIPMENT_NAME   = 'equipmentName',
  CITY_STATE       = 'cityState',
  CONTRACT_NAME    = 'contractName',
  PRODUCT_CATEGORY = 'productCategory'
}

export enum EOrderWorkflows {
  WT_ORDER  = 'wt_order',
  CPQ_ORDER = 'cpq_order',
  SAP_ORDER = 'sap_order',
}

export enum EContactUsTopic {
  GENERAL_REQUEST   = 'general-request',
  WRONG_ADDRESS     = 'wrong-address',
  MISSING_EQUIPMENT = 'missing-equipment',
  DIFFERENT_ROLE    = 'different-role',
}

export const FEATURE_TOGGLE: string = 'feature_toggle.';
export const FEATURE_CONFIG: string = 'config.';

export enum EFeatureToggles {
  // IMPORTANT: Only for boolean-valued feature toggle keys
  ADD_TO_CART_VIA_CSV                                            = 'add_to_cart_via_csv',
  ADD_TO_CART_VIA_SEARCHBAR                                      = 'add_to_cart_via_searchbar',
  ADOBE_ANALYTICS                                                = 'adobe_analytics',
  APPROVER_BUYER_FLOW                                            = 'approver_buyer_flow',
  APPROVER_HYPERLINK                                             = 'approver_hyperlink',
  ATTENTION_TO                                                   = 'attention_to',
  CART_EXPORT_AS_PDF                                             = 'cart_export_as_pdf',
  CHECKOUT_FILE_UPLOAD                                           = 'enable_file_upload_in_checkout',
  CHECKOUT_ITEM_AVAILABILITY                                     = 'checkout_page_item_availability',
  CHECKOUT_QUOTE_APPROVAL_MANDATORY                              = 'checkout_always_with_quote_approval',
  CHECKOUT_SET_DEFAULT_PO_NUMBER_FOR_ZERO_CART_PRICE             = 'checkout_set_default_po_number_for_zero_cart_price',
  CHECKOUT_SIDEBAR_CART_EXPORT_AS_PDF                            = 'checkout_sidebar_cart_export_as_pdf',
  CHECKOUT_SIDEBAR_SUBTITLE                                      = 'checkout_sidebar_subtitle',
  CHECKOUT_SIDEBAR_TITLE                                         = 'checkout_sidebar_title',
  CHECKOUT_TAX_PRICE                                             = 'checkout_tax_price',
  CONSUMABLE_ONLY_EQUIPMENT_PURCHASES                            = 'enable_consumable_only_equipment_purchases',
  CPQ                                                            = 'cpq',
  DELIVERY_DETAILS_SHIPPING_POLICY                               = 'delivery_details_shipping_policy',
  DISCOUNT_CODE_ENABLED                                          = 'sap.discount_code_enabled',
  DOWNLOAD_RETURN_DOCUMENT                                       = 'download_return_document',
  EQUIPMENT_COMPONENT_FILTER_ACTIVE_FOR_APPROVER_USER            = 'equipment_component_filter_active_for_approver_user',
  EQUIPMENT_COMPONENT_FILTER_ACTIVE_FOR_BROWSER_USER             = 'equipment_component_filter_active_for_browser_user',
  EQUIPMENT_COMPONENT_FILTER_ACTIVE_FOR_BUSINESSPARTNER_APPROVER = 'equipment_component_filter_active_for_businesspartner_approver',
  EQUIPMENT_COMPONENT_FILTER_ACTIVE_FOR_BUSINESSPARTNER_BUYER    = 'equipment_component_filter_active_for_businesspartner_buyer',
  EQUIPMENT_COMPONENT_FILTER_ACTIVE_FOR_CUSTOMER_ADMIN           = 'equipment_component_filter_active_for_customer_admin',
  EQUIPMENT_COMPONENT_FILTER_ACTIVE_FOR_ORDER_USER               = 'equipment_component_filter_active_for_order_user',
  EQUIPMENT_COMPONENT_FILTER_ACTIVE_FOR_SPAREPARTS_VIEW          = 'equipment_component_filter_active_for_spareparts_view',
  ENHANCED_EQUIPMENT_TYPE_FILTER                                 = 'enhanced_equipment_type_filter',
  EXCHANGEABLE_ITEM_CHECKOUT_PRICE                               = 'exchangeable_item_checkout_price',
  EXCHANGEABLE_ITEM_TAG                                          = 'exchangeable_item_tag',
  EXTERNAL_QUOTE_CHECKOUT                                        = 'external_quote_checkout',
  HARMONIZED_DELIVERY_DETAILS_PAGE                               = 'harmonized_delivery_details_page',
  HARMONIZED_MY_INSTALLED_BASE_PAGE                              = 'harmonized_my_installed_base_page',
  HARMONIZED_ORDER_REVIEW_PAGE                                   = 'harmonized_order_review_page',
  HARMONIZED_ORDER_THANK_YOU_PAGE                                = 'harmonized_order_thank_you_page',
  HARMONIZED_PURCHASE_ACTIVITY_PAGE                              = 'harmonized_purchase_activity_page',
  HOME_INSTALL_BASE                                              = 'home_install_base',
  INSTALL_BASE_ADDRESS_ANONYMIZE                                 = 'install_base_address_anonymize',
  MAINTENANCE                                                    = 'maintenance',
  MIXED_SAP_PRODUCT_IDENTIFIER_CART_RESTRICTION                  = 'mixed_sap_product_identifier_cart_restriction',
  MY_EQUIPMENT_COMPONENTS                                        = 'my_equipment_components',
  MY_INSTALLED_BASE_FLOW                                         = 'enable_my_installed_base_flow',
  NON_EQUIPMENT_PURCHASE                                         = 'without_equipment_purchases',
  OTHER_EQUIPMENT                                                = 'other_equipment',
  PDP_DELIVERY_AVAILABILITY                                      = 'pdp_delivery_availability',
  PDP_ESTIMATED_DELIVERY                                         = 'pdp_estimated_delivery',
  PREDEFINED_EQUIPMENT_FLOW                                      = 'predefined_equipment_flow',
  PREFERRED_PAYMENT                                              = 'preferred_payment_method',
  PREFILL_POC_PHONE_NUMBER                                       = 'prefill_poc_phone_number',
  PRODUCT_DISCONTINUED_STATUS                                    = 'product_discontinued_status',
  PRODUCT_PRICING_GROUP                                          = 'product_pricing_group',
  PRODUCT_VARIANTS                                               = 'product_variants',
  PURCHASE_ACTIVITY_ARCHIVE                                      = 'purchase_activity_archive',
  PURCHASE_ACTIVITY_DOWNLOAD_RETURN_DOCUMENT                     = 'purchase_activity_download_return_document',
  PURCHASE_ACTIVITY_EXPORT_AS_PDF                                = 'purchase_activity_export_as_pdf',
  PURCHASE_ACTIVITY_REORDER_ALL_ITEMS                            = 'purchase_activity_reorder_all_items',
  PURCHASE_ACTIVITY_SHOW_ORDER_DETAILS                           = 'purchase_activity_show_order_details',
  REQUEST_QUOTE_CLOSED_LOOP                                      = 'request_quote_closed_loop',
  RFQ_FLOW                                                       = 'rfq_flow',
  SAP_INTEGRATION                                                = 'sap_integration',
  SAP_INTEGRATION_REQUIRED                                       = 'sap_integration_required',
  SAP_P40                                                        = 'sap_p40',
  SAP_P40_NOTIFICATION_NUMBER                                    = 'sap_p40_notification_number',
  SAP_P40_PEAK_NUMBER                                            = 'sap_p40_peak_number',
  SAP_P40_SERIAL_NUMBER                                          = 'sap_p40_serial_number',
  SAP_P40_SOFTWARE_VERSION                                       = 'sap_p40_software_version',
  SAP_P58_EXTERNAL_ORDER_CREATION                                = 'sap_p58_external_order_creation',
  SDM_INTEGRATION                                                = 'sdm_integration',
  SHIPMENT_METHODS_RELOAD_REQUIRED                               = 'shipment_methods_reload_required',
  SHOP_CART_DELIVERY_AVAILABILITY                                = 'shop_cart_delivery_availability',
  SINGLE_LOGOUT                                                  = 'single_logout',
  SIPAYCE_INTEGRATION                                            = 'sipayce_integration',
  SPARE_PARTS                                                    = 'spare_parts',
  SUBMIT_RETURN_DOCUMENT                                         = 'submit_return_document',
  VERTEX_TAX_CALCULATION                                         = 'vertex_tax_calculation',
  WALK_ME                                                        = 'walk_me',
}

export enum ECheckoutFlows {
  REGULAR_FLOW        = 'regular_flow',
  GENERATE_QUOTE_FLOW = 'generate_quote_flow',
  REQUEST_QUOTE_FLOW  = 'request_quote_flow',
}

export enum ETermsAndConsentsType {
  GENERATE_QUOTE = 'generate_quote',
  CHECKOUT       = 'checkout',
}

export enum EFeatureSettings {
  // Intended for non-boolean values related to features
  WALK_ME_SCRIPT                    = 'walk_me_inner_script',
  EQUIPMENT_SELECTION_SESSION_LIMIT = 'equipment_selection_session_limit',
  PRICING_GROUP_FOR_CONSUMABLES     = 'pricing_group_for_consumables',
}

export enum EMessageType {
  INFO    = 'info',
  WARNING = 'warning',
  ERROR   = 'error',
}

export enum EAddressType {
  SOLD_TO = 'sold-to',
  SHIP_TO = 'ship-to',
  BILL_TO = 'bill-to',
  PAYER   = 'payer'
}

export enum ESapDiscountType {
  SAP_DISCOUNT          = 'sap-discount',
  SAP_DISCOUNT_SHIPPING = 'sap-discount-shipping',
}

export const FEATURE_SETTING_WALK_ME_SCRIPT: string = FEATURE_CONFIG + EFeatureSettings['WALK_ME_SCRIPT'];
export const FEATURE_SETTING_EQUIPMENT_SELECTION_SESSION_LIMIT: string = FEATURE_CONFIG + EFeatureSettings['EQUIPMENT_SELECTION_SESSION_LIMIT'];
export const CONFIG_PRICING_GROUP_FOR_CONSUMABLES: string = FEATURE_CONFIG + EFeatureSettings['PRICING_GROUP_FOR_CONSUMABLES'];
export const REGISTER_URL_KEY: string = 'app-bar.register-url';

export enum EDeliveryInputNames {
  ATTENTION_TO                     = 'attentionTo',
  BILL_TO_ADDRESS                  = 'billToAddress',
  BUSINESS_PARTNER_BILL_TO_ADDRESS = 'businessPartnerBillToAddress',
  COMMENT                          = 'comment',
  CUSTOMER                         = 'customer',
  DELIVERY_DATE                    = 'deliveryDate',
  DELIVERY_TIME                    = 'deliveryTime',
  DEPARTMENT                       = 'department',
  EMAIL                            = 'email',
  FIRST_NAME                       = 'firstName',
  FLOOR_OR_ROOM                    = 'floorOrRoom',
  LAST_NAME                        = 'lastName',
  NAME_ROW                         = 'nameRow',
  PHONE                            = 'phone',
  PROMO_CODE                       = 'promoCode',
  SELECTED_EQUIPMENT               = 'selectedEquipment',
  SHIPMENT_METHOD                  = 'shipmentMethod',
  SHIP_TO_ADDRESS                  = 'shipToAddress',
  SOLD_TO_ACCOUNT                  = 'soldToAccount',
  TAX_NUMBER                       = 'taxNumber',
}

export enum EApproverInputNames {
  APPROVER   = 'approver',
  COMMENT    = 'comment',
  PO_NUMBER  = 'poNumber',
  PROMO_CODE = 'promoCode',
}

export enum ECheckoutSections {
  ADDRESSES                         = 'addresses',
  PAYMENT_METHODS                   = 'payment_methods',
  DISCOUNTS                         = 'discounts',
  BILLING_TAX_NUMBER                = 'billing_tax_number',
  PO_NUMBER                         = 'po_number',
  DELIVERY_ADDRESS_AND_INSTRUCTIONS = 'delivery_address_and_instructions',
  ORDER_SUMMARY                     = 'order_summary',
  VAT_ID_TAX_NUMBER                 = 'vat_id_tax_number',
}

export enum EThankYouPageFieldNames {
  FUNCTIONAL_LOCATION = 'functionalLocation',
  SOLD_TO_ADDRESS     = 'soldToAddress',
  SHIP_TO_ADDRESS     = 'shipToAddress',
  BILL_TO_ADDRESS     = 'billToAddress',
  PAYER_ADDRESS       = 'payerAddress',
  EMAIL               = 'email',
  PHONE_NUMBER        = 'phoneNumber',
  SHIPMENT_METHOD     = 'shipmentMethod',
  PAYMENT_METHOD      = 'paymentMethod',
  TAX_NUMBER          = 'taxNumber',
  PO_NUMBER           = 'poNumber',
  RETURN_ORDER_NUMBER = 'returnOrderNumber',
}

export enum EThankYouPagePriceFieldNames {
  SUBTOTAL  = 'subtotal',
  EXPENSE   = 'expense',
  TAX       = 'tax',
  DISCOUNTS = 'discounts',
}

export enum ECartStatus {
  APPROVED = 'approved',
  CANCELED = 'canceled',
  DECLINED = 'declined',
  WAITING  = 'waiting',
}

export enum EOrderStatusTypes {
  INTERNAL_ORDER_ERROR     = 'internal-order-error',
  PAYMENT_ABORTED          = 'payment-aborted',
  PAYMENT_FAILED           = 'payment-failed',
  PAYMENT_PENDING          = 'payment-pending',
  ORDER_FINALIZATION_ERROR = 'order-finalization-error', // possible SAP error
  ORDER_PENDING            = 'order-pending', // SAP call is still pending
}

export enum EPriceModes {
  GROSS_MODE = 'GROSS_MODE',
  NET_MODE   = 'NET_MODE',
}

export const KGM: string = 'KGM';
export const LBS: string = 'LBS';
export const KG: string = 'KG';
export const WEIGHT_KGM_LBS_CONVERSION_COEFFICIENT: number = 0.45359;

export enum EPriceVariant {
  WITHOUT_REPLACEMENT = 'without-replacement',
  WITH_REPLACEMENT    = 'with-replacement',
}
